// src/App.jsx

import './index.css';
import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Marketing
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Hero from './pages/Hero';
import Features from './pages/Features';
import HowItWorks from './pages/HowItWorks';
import Pricing from './pages/Pricing';
import Vouchers from './components/Vouchers';
import FAQ from './pages/FAQ';
import CTA from './pages/CTA';
import ComingSoonModal from './components/ui/ComingSoonModal';
import BackToTop from './components/ui/BackToTop';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Auth
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

import PortalLayout from './pages/PortalLayout';

// Only allow redirects to internal portal paths
function safeNext(raw) {
  if (!raw) return '/portal';
  return raw.startsWith('/portal') ? raw : '/portal';
}

// Redirect to login if not authenticated
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
}

// Redirect to portal (or ?next=) if already logged in
function RedirectIfAuthed({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const params   = new URLSearchParams(location.search);
  const next     = safeNext(location.state?.from || params.get('next'));

  if (loading) return null;
  if (user) return <Navigate to={next} replace />;

  return children;
}

function MarketingHome() {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: theme.background }}>
      <Header onDownloadClick={() => setIsModalOpen(true)} />
      <main>
        <Hero     onDownloadClick={() => setIsModalOpen(true)} />
        <Features />
        <HowItWorks />
        <Pricing  onDownloadClick={() => setIsModalOpen(true)} />
        <Vouchers />
        <FAQ />
        <CTA      onDownloadClick={() => setIsModalOpen(true)} />
      </main>
      <Footer />
      <ComingSoonModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <BackToTop />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/"               element={<MarketingHome />} />
      <Route path="/login"          element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password"  element={<ResetPassword />} />
      <Route path="/portal/*"       element={<RequireAuth><PortalLayout /></RequireAuth>} />
      <Route path="*"               element={<Navigate to="/" replace />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}